'use client';

import { DisclosureAddOnProps, DisclosuresItem, DisclosureEntry, ModalEntry } from './types';
import { toDocumentWithEmbeddedEntries } from 'libs/contentful/toDocumentWithEmbeddedEntries';
import { getEntryNativeFields } from 'utils/entryNativeFields';
import { getModuleSectionName } from 'utils/modules';
import { KeyLinePosition, Margins } from '@/types/constants';
import { cn, getSectionProps } from '@/utils/styles';
import Disclosure from '@/components/DisclosureGroupings/Disclosure';
import BaseModal from '@/components/shared/BaseModal/BaseModal';
import KeyLine from '@/components/shared/KeyLine';
import { useConsumedDisclosures, useDisclosureNumberingMap, useDisclosureContext } from '@/contexts/DisclosureProvider';
import { useEffect, useMemo } from 'react';

function isEntryType(item: DisclosuresItem, contentTypeId: 'modal'): item is ModalEntry;
function isEntryType(item: DisclosuresItem, contentTypeId: 'disclosure'): item is DisclosureEntry;
function isEntryType(item: DisclosuresItem, contentTypeId: 'modal' | 'disclosure') {
  return item.sys.contentType.sys.id === contentTypeId;
}

const getMarginStyles = (margins?: Margins, showTopKeyline?: boolean) => {
  // When keyline is shown, we need to adjust top padding
  if (showTopKeyline) {
    // Keyed by an enum so the annotation enforces exhaustiveness; dropping it would let a
    // new enum member go unhandled silently.
    // oxlint-disable-next-line anti-slop/no-known-value-widening
    const marginStyles: { [key in Margins]: string } = {
      [Margins.NoTop]: 'pt-0 pb-6',
      [Margins.NoBottom]: 'pt-4 pb-0', // Add some space after keyline
      [Margins.NoMargins]: 'pt-4 pb-2',
      [Margins.Centered]: 'pt-4 pb-6', // Reduced top padding when keyline present
    };
    return margins ? marginStyles[margins] : marginStyles[Margins.NoMargins];
  }

  // Keyed by an enum so the annotation enforces exhaustiveness; dropping it would let a
  // new enum member go unhandled silently.
  // oxlint-disable-next-line anti-slop/no-known-value-widening
  const marginStyles: { [key in Margins]: string } = {
    [Margins.NoTop]: 'pt-0 pb-6',
    [Margins.NoBottom]: 'pt-6 pb-0',
    [Margins.NoMargins]: 'py-2',
    [Margins.Centered]: 'py-6',
  };

  return margins ? marginStyles[margins] : marginStyles[Margins.NoMargins];
};

/**
 * DisclosureAddOn - A module that displays specific disclosures inline
 *
 * When disclosures are shown in this component, they are "consumed" and
 * will NOT be duplicated in the footer's DynamicDisclosures component.
 *
 * Auto-numbering is only shown when the disclosure is embedded in content above.
 * Symbols are always shown when the disclosure has one, even if only in this add-on.
 */
const DisclosureAddOn = (props: DisclosureAddOnProps) => {
  const { sys } = props;
  const { color, margins, keyLine, disclosures } = getEntryNativeFields(props);
  const sectionName = getModuleSectionName(props);

  const renderable = useMemo(
    () => (disclosures ?? []).filter((item): item is DisclosuresItem => !!item && 'fields' in item),
    [disclosures],
  );
  const { consumeDisclosures } = useConsumedDisclosures();
  const disclosureNumberingMap = useDisclosureNumberingMap();
  const { allDisclosures } = useDisclosureContext();
  const showTopKeyline = keyLine === KeyLinePosition.Top;

  // Register disclosures (not modals) as "consumed" so they won't appear in DynamicDisclosures
  useEffect(() => {
    const disclosureIds = renderable.filter(item => isEntryType(item, 'disclosure')).map(item => item.sys.id);
    if (disclosureIds.length > 0) {
      return consumeDisclosures(sys.id, disclosureIds);
    }
  }, [renderable, consumeDisclosures, sys.id]);

  if (!renderable.length) {
    return null;
  }

  // Check if a disclosure is embedded somewhere in the page content
  const isDisclosureEmbedded = (disclosureId: string): boolean => {
    return allDisclosures.some(d => d.sys?.id === disclosureId);
  };

  return (
    <section {...getSectionProps(color, undefined, sectionName)} data-testid="disclosureAddOn">
      <div className="content-wrapper">
        {showTopKeyline && <KeyLine />}
        <div className={cn('flex flex-col gap-y-2', getMarginStyles(margins, showTopKeyline))}>
          {renderable.map(item => {
            if (isEntryType(item, 'modal')) {
              const { headline, disclaimerText, copy, iconPosition, iconOnlyLink } = item.fields;
              return (
                <BaseModal
                  key={item.sys.id}
                  sys={item.sys}
                  title={headline}
                  body={copy ? toDocumentWithEmbeddedEntries(copy, { parentTypename: 'Modal', fieldName: 'copy' }) : ''}
                  openButtonText={disclaimerText}
                  iconPosition={iconPosition}
                  iconOnlyLink={iconOnlyLink}
                />
              );
            }

            if (isEntryType(item, 'disclosure')) {
              const { symbol, copy, disclosureItem, disclosureSymbol } = item.fields;
              const autoNumber =
                isDisclosureEmbedded(item.sys.id) && !symbol ? disclosureNumberingMap.get(item.sys.id) : undefined;

              return (
                <Disclosure
                  key={item.sys.id}
                  symbol={symbol}
                  copy={
                    copy
                      ? toDocumentWithEmbeddedEntries(copy, { parentTypename: 'Disclosure', fieldName: 'copy' })
                      : undefined
                  }
                  disclosureSymbol={
                    disclosureSymbol && 'fields' in disclosureSymbol
                      ? { sys: disclosureSymbol.sys, symbol: disclosureSymbol.fields.symbol }
                      : undefined
                  }
                  disclosureItem={
                    disclosureItem && 'fields' in disclosureItem && disclosureItem.fields.copy
                      ? {
                          sys: disclosureItem.sys,
                          copy: toDocumentWithEmbeddedEntries(disclosureItem.fields.copy, {
                            parentTypename: 'DisclosureItem',
                            fieldName: 'copy',
                          }),
                        }
                      : undefined
                  }
                  autoNumber={autoNumber}
                />
              );
            }

            return null;
          })}
        </div>
      </div>
    </section>
  );
};

export default DisclosureAddOn;
